import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import speakingService from '@/services/speaking/speakingService';
import analyticsService from '@/services/speaking/analyticsService';
import phonemeService from '@/services/speaking/phonemeService';
import practiceService from '@/services/speaking/practiceService';
import notificationService from '@/services/notificationService';
import {
    // Speaking Exercises
    fetchExercisesRequest,
    fetchExercisesSuccess,
    fetchExercisesFailure,
    fetchExerciseByIdRequest,
    fetchExerciseByIdSuccess,
    fetchExerciseByIdFailure,
    fetchPhonemeDetailsRequest,
    fetchPhonemeDetailsSuccess,
    fetchPhonemeDetailsFailure,
    fetchRecommendedExercisesRequest,
    fetchRecommendedExercisesSuccess,
    fetchRecommendedExercisesFailure,
    rateRecommendationRequest,
    rateRecommendationSuccess,
    rateRecommendationFailure,

    // Pronunciation Alerts
    fetchMispronunciationAlertsRequest,
    fetchMispronunciationAlertsSuccess,
    fetchMispronunciationAlertsFailure,
    createPronunciationAlertRequest,
    createPronunciationAlertSuccess,
    createPronunciationAlertFailure,
    dismissPronunciationAlertRequest,
    dismissPronunciationAlertSuccess,
    dismissPronunciationAlertFailure,

    // Phoneme Playback
    fetchPhonemeTimingsRequest,
    fetchPhonemeTimingsSuccess,
    fetchPhonemeTimingsFailure,
    generatePhonemeAudioRequest,
    generatePhonemeAudioSuccess,
    generatePhonemeAudioFailure,

    // Waveform Analysis
    fetchWaveformDataRequest,
    fetchWaveformDataSuccess,
    fetchWaveformDataFailure,

    // GPT Feedback
    fetchConversationalFeedbackRequest,
    fetchConversationalFeedbackSuccess,
    fetchConversationalFeedbackFailure,
    updateFeedbackToneRequest,
    updateFeedbackToneSuccess,
    updateFeedbackToneFailure,

    // Audio Comparison
    compareAudioSimilarityRequest,
    compareAudioSimilaritySuccess,
    compareAudioSimilarityFailure,

    // Avatar Practice
    fetchAvatarDemonstrationRequest,
    fetchAvatarDemonstrationSuccess,
    fetchAvatarDemonstrationFailure,
} from '@/store/slices/speaking';

import {
    // Practice Flow Actions
    startPracticeFlowRequest,
    startPracticeFlowSuccess,
    startPracticeFlowFailure,
    analyzePronunciationRequest,
    analyzePronunciationSuccess,
    analyzePronunciationFailure,
    generateFeedbackRequest,
    generateFeedbackSuccess,
    generateFeedbackFailure,
    generateReferenceAudioRequest,
    generateReferenceAudioSuccess,
    generateReferenceAudioFailure,
    generateAvatarPracticeRequest,
    generateAvatarPracticeSuccess,
    generateAvatarPracticeFailure,
    startPracticeSessionRequest,
    startPracticeSessionSuccess,
    startPracticeSessionFailure,
    endPracticeSessionRequest,
    endPracticeSessionSuccess,
    endPracticeSessionFailure,
} from '@/store/slices/speaking/practiceSlice';

import {
    // Sessions
    fetchSessionsRequest,
    fetchSessionsSuccess,
    fetchSessionsFailure,
    fetchSessionByIdRequest,
    fetchSessionByIdSuccess,
    fetchSessionByIdFailure,
    startSessionRequest,
    startSessionSuccess,
    startSessionFailure,
    endSessionRequest,
    endSessionSuccess,
    endSessionFailure,
    completeSessionRequest,
    completeSessionSuccess,
    completeSessionFailure,
    uploadAudioRequest,
    uploadAudioSuccess,
    uploadAudioFailure,
} from '@/store/slices/speaking/sessionsSlice';

import {
    // Phonemes
    fetchPhonemesRequest,
    fetchPhonemesSuccess,
    fetchPhonemesFailure,
    fetchPhonemeByIdRequest,
    fetchPhonemeByIdSuccess,
    fetchPhonemeByIdFailure,
    fetchPhonemeSkillMapRequest,
    fetchPhonemeSkillMapSuccess,
    fetchPhonemeSkillMapFailure,
} from '@/store/slices/speaking/phonemesSlice';

import {
    // Analytics
    fetchUserProfileRequest,
    fetchUserProfileSuccess,
    fetchUserProfileFailure,
    fetchAnalyticsRequest,
    fetchAnalyticsSuccess,
    fetchAnalyticsFailure,
    fetchProgressTimelineRequest,
    fetchProgressTimelineSuccess,
    fetchProgressTimelineFailure,
} from '@/store/slices/speaking/analyticsSlice';

import { ExerciseFilters, StartSessionRequest, AudioUploadRequest } from '@/services/speaking/types';

// === PRACTICE FLOW SAGAS ===

function* startPracticeFlowSaga(
    action: PayloadAction<{
        audio_blob: Blob;
        target_text: string;
        exercise_id?: string;
        feedback_tone?: 'playful' | 'serious' | 'explanatory' | 'encouraging';
        avatar_type?: '2d' | '3d';
    }>,
): SagaIterator {
    try {
        const {
            audio_blob,
            target_text,
            exercise_id,
            feedback_tone = 'encouraging',
            avatar_type = '2d',
        } = action.payload;

        // Execute the full practice flow
        const response = yield call(
            practiceService.executePracticeFlow.bind(practiceService),
            audio_blob,
            target_text,
            exercise_id,
            feedback_tone,
            avatar_type,
        );

        yield put(startPracticeFlowSuccess(response));

        // Note: Notification service integration can be added later if needed
        console.log('Practice flow completed successfully');
    } catch (error: any) {
        yield put(startPracticeFlowFailure(error.message || 'Practice flow failed'));
    }
}

function* analyzePronunciationSaga(
    action: PayloadAction<{
        audio_blob: Blob;
        target_text: string;
        exercise_id?: string;
        content_type: string;
    }>,
): SagaIterator {
    try {
        const response = yield call(practiceService.analyzePronunciation, action.payload);
        yield put(analyzePronunciationSuccess(response));
    } catch (error: any) {
        yield put(analyzePronunciationFailure(error.message || 'Pronunciation analysis failed'));
    }
}

function* generateFeedbackSaga(
    action: PayloadAction<{
        pronunciation_errors: any[];
        overall_score: number;
        target_text: string;
        feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
    }>,
): SagaIterator {
    try {
        const response = yield call(practiceService.generateConversationalFeedback.bind(practiceService), {
            pronunciation_errors: action.payload.pronunciation_errors,
            overall_score: action.payload.overall_score,
            user_text: action.payload.target_text, // In real scenario, this would be transcribed
            target_text: action.payload.target_text,
            feedback_tone: action.payload.feedback_tone,
        });
        yield put(generateFeedbackSuccess(response));
    } catch (error: any) {
        yield put(generateFeedbackFailure(error.message || 'Feedback generation failed'));
    }
}

function* generateReferenceAudioSaga(
    action: PayloadAction<{
        user_audio_url: string;
        target_text: string;
        target_phonemes: string[];
    }>,
): SagaIterator {
    try {
        const response = yield call(practiceService.generateReferenceAudio.bind(practiceService), action.payload);
        yield put(generateReferenceAudioSuccess(response));
    } catch (error: any) {
        yield put(generateReferenceAudioFailure(error.message || 'Reference audio generation failed'));
    }
}

function* generateAvatarPracticeSaga(
    action: PayloadAction<{
        target_text: string;
        problematic_phonemes: string[];
        user_level: string;
        avatar_type: '2d' | '3d';
        feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
    }>,
): SagaIterator {
    try {
        const response = yield call(practiceService.generateAvatarPractice.bind(practiceService), action.payload);
        yield put(generateAvatarPracticeSuccess(response));
    } catch (error: any) {
        yield put(generateAvatarPracticeFailure(error.message || 'Avatar practice generation failed'));
    }
}

function* startPracticeSessionSaga(action: PayloadAction<{ exercise_id?: string }>): SagaIterator {
    try {
        const response = yield call(
            practiceService.startPracticeSession.bind(practiceService),
            action.payload.exercise_id,
        );
        yield put(startPracticeSessionSuccess(response));
    } catch (error: any) {
        yield put(startPracticeSessionFailure(error.message || 'Failed to start practice session'));
    }
}

function* endPracticeSessionSaga(action: PayloadAction<{ session_id: string; session_data: any }>): SagaIterator {
    try {
        const { session_id, session_data } = action.payload;
        const response = yield call(practiceService.endPracticeSession.bind(practiceService), session_id, session_data);
        yield put(endPracticeSessionSuccess(response));
    } catch (error: any) {
        yield put(endPracticeSessionFailure(error.message || 'Failed to end practice session'));
    }
}

// === SPEAKING EXERCISES SAGAS ===

function* fetchExercisesSaga(action: PayloadAction<ExerciseFilters | undefined>): SagaIterator {
    try {
        const response = yield call(speakingService.getExercises, action.payload);
        yield put(fetchExercisesSuccess(response));
    } catch (error: any) {
        yield put(fetchExercisesFailure(error.message || 'Failed to fetch exercises'));
    }
}

function* fetchExerciseByIdSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.getExerciseById, action.payload);
        yield put(fetchExerciseByIdSuccess(response));
    } catch (error: any) {
        yield put(fetchExerciseByIdFailure(error.message || 'Failed to fetch exercise'));
    }
}

function* fetchPhonemeDetailsSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.getExercisePhonemeDetails, action.payload);
        yield put(fetchPhonemeDetailsSuccess(response));
    } catch (error: any) {
        yield put(fetchPhonemeDetailsFailure(error.message || 'Failed to fetch phoneme details'));
    }
}

function* fetchRecommendedExercisesSaga(): SagaIterator {
    try {
        const response = yield call(speakingService.getRecommendedExercises);
        yield put(fetchRecommendedExercisesSuccess(response));
    } catch (error: any) {
        yield put(fetchRecommendedExercisesFailure(error.message || 'Failed to fetch recommendations'));
    }
}

function* rateRecommendationSaga(action: PayloadAction<{ recommendationId: string; rating: number }>): SagaIterator {
    try {
        const { recommendationId, rating } = action.payload;
        const response = yield call(speakingService.rateRecommendation, recommendationId, rating);
        yield put(rateRecommendationSuccess(response));
    } catch (error: any) {
        yield put(rateRecommendationFailure(error.message || 'Failed to rate recommendation'));
    }
}

// === PRONUNCIATION ALERTS SAGAS ===

function* fetchMispronunciationAlertsSaga(): SagaIterator {
    try {
        const response = yield call(speakingService.getMispronunciationAlerts);
        yield put(fetchMispronunciationAlertsSuccess(response));
    } catch (error: any) {
        yield put(fetchMispronunciationAlertsFailure(error.message || 'Failed to fetch alerts'));
    }
}

function* createPronunciationAlertSaga(
    action: PayloadAction<{
        phoneme: string;
        error_type: string;
        severity: 'minor' | 'moderate' | 'major';
        message: string;
    }>,
): SagaIterator {
    try {
        const response = yield call(speakingService.createPronunciationAlert, action.payload);
        yield put(createPronunciationAlertSuccess(response));
    } catch (error: any) {
        yield put(createPronunciationAlertFailure(error.message || 'Failed to create alert'));
    }
}

function* dismissPronunciationAlertSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.dismissPronunciationAlert, action.payload);
        yield put(dismissPronunciationAlertSuccess(response));
    } catch (error: any) {
        yield put(dismissPronunciationAlertFailure(error.message || 'Failed to dismiss alert'));
    }
}

// === PHONEME PLAYBACK SAGAS ===

function* fetchPhonemeTimingsSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.getPhonemeTimings, action.payload);
        yield put(fetchPhonemeTimingsSuccess(response));
    } catch (error: any) {
        yield put(fetchPhonemeTimingsFailure(error.message || 'Failed to fetch phoneme timings'));
    }
}

function* generatePhonemeAudioSaga(action: PayloadAction<{ phoneme: string; voice: 'male' | 'female' }>): SagaIterator {
    try {
        const { phoneme, voice } = action.payload;
        const response = yield call(speakingService.generatePhonemeAudio, phoneme, voice);
        yield put(generatePhonemeAudioSuccess(response));
    } catch (error: any) {
        yield put(generatePhonemeAudioFailure(error.message || 'Failed to generate phoneme audio'));
    }
}

// === WAVEFORM ANALYSIS SAGAS ===

function* fetchWaveformDataSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.getWaveformData, action.payload);
        yield put(fetchWaveformDataSuccess(response));
    } catch (error: any) {
        yield put(fetchWaveformDataFailure(error.message || 'Failed to fetch waveform data'));
    }
}

// === GPT FEEDBACK SAGAS ===

function* fetchConversationalFeedbackSaga(
    action: PayloadAction<{ attemptId: string; tone?: 'playful' | 'serious' | 'explanatory' | 'encouraging' }>,
): SagaIterator {
    try {
        const { attemptId, tone = 'encouraging' } = action.payload;
        const response = yield call(speakingService.getConversationalFeedback, attemptId, tone);
        yield put(fetchConversationalFeedbackSuccess(response));
    } catch (error: any) {
        yield put(fetchConversationalFeedbackFailure(error.message || 'Failed to fetch feedback'));
    }
}

function* updateFeedbackToneSaga(action: PayloadAction<{ tone: string }>): SagaIterator {
    try {
        // Note: This needs a session ID - should be updated to get from Redux state
        const response = yield call(
            speakingService.updateFeedbackTone.bind(speakingService),
            'current-session',
            action.payload.tone as any,
        );
        yield put(updateFeedbackToneSuccess(response));
    } catch (error: any) {
        yield put(updateFeedbackToneFailure(error.message || 'Failed to update feedback tone'));
    }
}

// === AUDIO COMPARISON SAGAS ===

function* compareAudioSimilaritySaga(
    action: PayloadAction<{ userAudioUrl: string; referenceAudioUrl: string }>,
): SagaIterator {
    try {
        const { userAudioUrl, referenceAudioUrl } = action.payload;
        const response = yield call(speakingService.compareAudioSimilarity, userAudioUrl, referenceAudioUrl);
        yield put(compareAudioSimilaritySuccess(response));
    } catch (error: any) {
        yield put(compareAudioSimilarityFailure(error.message || 'Failed to compare audio'));
    }
}

// === AVATAR PRACTICE SAGAS ===

function* fetchAvatarDemonstrationSaga(
    action: PayloadAction<{ exerciseId: string; avatarType: '2d' | '3d' }>,
): SagaIterator {
    try {
        const { exerciseId, avatarType } = action.payload;
        const response = yield call(speakingService.getAvatarDemonstration, exerciseId, avatarType);
        yield put(fetchAvatarDemonstrationSuccess(response));
    } catch (error: any) {
        yield put(fetchAvatarDemonstrationFailure(error.message || 'Failed to fetch avatar demonstration'));
    }
}

// === SESSIONS SAGAS ===

function* fetchSessionsSaga(action: PayloadAction<{ page?: number; pageSize?: number }>): SagaIterator {
    try {
        const { page = 1, pageSize = 20 } = action.payload;
        const response = yield call(speakingService.getSessions, page, pageSize);
        yield put(fetchSessionsSuccess(response));
    } catch (error: any) {
        yield put(fetchSessionsFailure(error.message || 'Failed to fetch sessions'));
    }
}

function* fetchSessionByIdSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.getSessionById, action.payload);
        yield put(fetchSessionByIdSuccess(response));
    } catch (error: any) {
        yield put(fetchSessionByIdFailure(error.message || 'Failed to fetch session'));
    }
}

function* startSessionSaga(action: PayloadAction<StartSessionRequest>): SagaIterator {
    try {
        const response = yield call(speakingService.startSession, action.payload);
        yield put(startSessionSuccess(response));
    } catch (error: any) {
        yield put(startSessionFailure(error.message || 'Failed to start session'));
    }
}

function* endSessionSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(speakingService.endSession, action.payload);
        yield put(endSessionSuccess(response));
    } catch (error: any) {
        yield put(endSessionFailure(error.message || 'Failed to end session'));
    }
}

export function* completeSessionSaga(action: PayloadAction<{ sessionId: string; completionData?: any }>): SagaIterator {
    try {
        const { sessionId, completionData } = action.payload;
        const response = yield call(speakingService.completeSession, sessionId, completionData);
        yield put(completeSessionSuccess(response));
    } catch (error: any) {
        yield put(completeSessionFailure(error.message || 'Failed to complete session'));
    }
}

function* uploadAudioSaga(action: PayloadAction<AudioUploadRequest>): SagaIterator {
    try {
        const response = yield call(speakingService.uploadAudio, action.payload);
        yield put(uploadAudioSuccess(response));
    } catch (error: any) {
        yield put(uploadAudioFailure(error.message || 'Failed to upload audio'));
    }
}

// === PHONEMES SAGAS ===

function* fetchPhonemesSaga(action: PayloadAction<any>): SagaIterator {
    try {
        const response = yield call(phonemeService.getPhonemes, action.payload);
        yield put(fetchPhonemesSuccess(response));
    } catch (error: any) {
        yield put(fetchPhonemesFailure(error.message || 'Failed to fetch phonemes'));
    }
}

function* fetchPhonemeByIdSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(phonemeService.getPhonemeById, action.payload);
        yield put(fetchPhonemeByIdSuccess(response));
    } catch (error: any) {
        yield put(fetchPhonemeByIdFailure(error.message || 'Failed to fetch phoneme'));
    }
}

function* fetchPhonemeSkillMapSaga(): SagaIterator {
    try {
        const response = yield call(phonemeService.getPhonemeSkillMap);
        yield put(fetchPhonemeSkillMapSuccess(response));
    } catch (error: any) {
        yield put(fetchPhonemeSkillMapFailure(error.message || 'Failed to fetch skill map'));
    }
}

// === ANALYTICS SAGAS ===

function* fetchUserProfileSaga(): SagaIterator {
    try {
        const response = yield call(analyticsService.getUserPronunciationProfile);
        yield put(fetchUserProfileSuccess(response));
    } catch (error: any) {
        yield put(fetchUserProfileFailure(error.message || 'Failed to fetch user profile'));
    }
}

function* fetchAnalyticsSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const response = yield call(analyticsService.getSpeakingAnalytics, action.payload || 30);
        yield put(fetchAnalyticsSuccess(response));
    } catch (error: any) {
        yield put(fetchAnalyticsFailure(error.message || 'Failed to fetch analytics'));
    }
}

function* fetchProgressTimelineSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const response = yield call(analyticsService.getProgressTimeline, action.payload);
        yield put(fetchProgressTimelineSuccess(response));
    } catch (error: any) {
        yield put(fetchProgressTimelineFailure(error.message || 'Failed to fetch timeline'));
    }
}

// === ERROR HANDLERS ===

function* handlePracticeFlowError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        console.error('Practice Flow Error:', error);
        yield put(startPracticeFlowFailure(error.message || 'Practice flow failed'));
    }
}

function* handleExercisesError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        console.error('Exercises Error:', error);
        yield put(fetchExercisesFailure(error.message || 'Failed to fetch exercises'));
    }
}

function* handleExerciseError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        console.error('Exercise Error:', error);
        yield put(fetchExerciseByIdFailure(error.message || 'Failed to fetch exercise'));
    }
}

// Additional error handlers...
function* handlePhonemeDetailsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchPhonemeDetailsFailure(error.message || 'Failed to fetch phoneme details'));
    }
}

function* handleRecommendationsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchRecommendedExercisesFailure(error.message || 'Failed to fetch recommendations'));
    }
}

function* handleRatingError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(rateRecommendationFailure(error.message || 'Failed to rate recommendation'));
    }
}

function* handleAlertsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchMispronunciationAlertsFailure(error.message || 'Failed to fetch alerts'));
    }
}

function* handleCreateAlertError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(createPronunciationAlertFailure(error.message || 'Failed to create alert'));
    }
}

function* handleDismissAlertError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(dismissPronunciationAlertFailure(error.message || 'Failed to dismiss alert'));
    }
}

function* handlePhonemeTimingsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchPhonemeTimingsFailure(error.message || 'Failed to fetch phoneme timings'));
    }
}

function* handleGeneratePhonemeAudioError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(generatePhonemeAudioFailure(error.message || 'Failed to generate phoneme audio'));
    }
}

function* handleWaveformError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchWaveformDataFailure(error.message || 'Failed to fetch waveform data'));
    }
}

function* handleFeedbackError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchConversationalFeedbackFailure(error.message || 'Failed to fetch feedback'));
    }
}

function* handleUpdateFeedbackToneError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(updateFeedbackToneFailure(error.message || 'Failed to update feedback tone'));
    }
}

function* handleAudioComparisonError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(compareAudioSimilarityFailure(error.message || 'Failed to compare audio'));
    }
}

function* handleAvatarDemoError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchAvatarDemonstrationFailure(error.message || 'Failed to fetch avatar demonstration'));
    }
}

function* handleSessionsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchSessionsFailure(error.message || 'Failed to fetch sessions'));
    }
}

function* handleSessionError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchSessionByIdFailure(error.message || 'Failed to fetch session'));
    }
}

function* handleStartSessionError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(startSessionFailure(error.message || 'Failed to start session'));
    }
}

function* handleEndSessionError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(endSessionFailure(error.message || 'Failed to end session'));
    }
}

function* handleCompleteSessionError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(completeSessionFailure(error.message || 'Failed to complete session'));
    }
}

function* handleUploadAudioError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(uploadAudioFailure(error.message || 'Failed to upload audio'));
    }
}

function* handlePhonemesError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchPhonemesFailure(error.message || 'Failed to fetch phonemes'));
    }
}

function* handlePhonemeByIdError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchPhonemeByIdFailure(error.message || 'Failed to fetch phoneme'));
    }
}

function* handlePhonemeSkillMapError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchPhonemeSkillMapFailure(error.message || 'Failed to fetch skill map'));
    }
}

function* handleUserProfileError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchUserProfileFailure(error.message || 'Failed to fetch user profile'));
    }
}

function* handleAnalyticsError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchAnalyticsFailure(error.message || 'Failed to fetch analytics'));
    }
}

function* handleProgressTimelineError(saga: any, action: any): SagaIterator {
    try {
        yield call(saga, action);
    } catch (error: any) {
        yield put(fetchProgressTimelineFailure(error.message || 'Failed to fetch timeline'));
    }
}

// === ROOT SAGA ===

export default function* speakingSaga(): SagaIterator {
    yield all([
        // Practice Flow Sagas
        takeLatest(startPracticeFlowRequest.type, handlePracticeFlowError, startPracticeFlowSaga),
        takeLatest(analyzePronunciationRequest.type, handlePracticeFlowError, analyzePronunciationSaga),
        takeLatest(generateFeedbackRequest.type, handlePracticeFlowError, generateFeedbackSaga),
        takeLatest(generateReferenceAudioRequest.type, handlePracticeFlowError, generateReferenceAudioSaga),
        takeLatest(generateAvatarPracticeRequest.type, handlePracticeFlowError, generateAvatarPracticeSaga),
        takeEvery(startPracticeSessionRequest.type, handlePracticeFlowError, startPracticeSessionSaga),
        takeEvery(endPracticeSessionRequest.type, handlePracticeFlowError, endPracticeSessionSaga),

        // Speaking Exercises
        takeLatest(fetchExercisesRequest.type, handleExercisesError, fetchExercisesSaga),
        takeLatest(fetchExerciseByIdRequest.type, handleExerciseError, fetchExerciseByIdSaga),
        takeLatest(fetchPhonemeDetailsRequest.type, handlePhonemeDetailsError, fetchPhonemeDetailsSaga),
        takeLatest(fetchRecommendedExercisesRequest.type, handleRecommendationsError, fetchRecommendedExercisesSaga),
        takeEvery(rateRecommendationRequest.type, handleRatingError, rateRecommendationSaga),

        // Pronunciation Alerts
        takeLatest(fetchMispronunciationAlertsRequest.type, handleAlertsError, fetchMispronunciationAlertsSaga),
        takeEvery(createPronunciationAlertRequest.type, handleCreateAlertError, createPronunciationAlertSaga),
        takeEvery(dismissPronunciationAlertRequest.type, handleDismissAlertError, dismissPronunciationAlertSaga),

        // Phoneme Playback
        takeLatest(fetchPhonemeTimingsRequest.type, handlePhonemeTimingsError, fetchPhonemeTimingsSaga),
        takeEvery(generatePhonemeAudioRequest.type, handleGeneratePhonemeAudioError, generatePhonemeAudioSaga),

        // Waveform Analysis
        takeLatest(fetchWaveformDataRequest.type, handleWaveformError, fetchWaveformDataSaga),

        // GPT Feedback
        takeLatest(fetchConversationalFeedbackRequest.type, handleFeedbackError, fetchConversationalFeedbackSaga),
        takeEvery(updateFeedbackToneRequest.type, handleUpdateFeedbackToneError, updateFeedbackToneSaga),

        // Audio Comparison
        takeEvery(compareAudioSimilarityRequest.type, handleAudioComparisonError, compareAudioSimilaritySaga),

        // Avatar Practice
        takeLatest(fetchAvatarDemonstrationRequest.type, handleAvatarDemoError, fetchAvatarDemonstrationSaga),

        // Sessions
        takeLatest(fetchSessionsRequest.type, handleSessionsError, fetchSessionsSaga),
        takeLatest(fetchSessionByIdRequest.type, handleSessionError, fetchSessionByIdSaga),
        takeEvery(startSessionRequest.type, handleStartSessionError, startSessionSaga),
        takeEvery(endSessionRequest.type, handleEndSessionError, endSessionSaga),
        takeEvery(completeSessionRequest.type, handleCompleteSessionError, completeSessionSaga),
        takeEvery(uploadAudioRequest.type, handleUploadAudioError, uploadAudioSaga),

        // Phonemes
        takeLatest(fetchPhonemesRequest.type, handlePhonemesError, fetchPhonemesSaga),
        takeLatest(fetchPhonemeByIdRequest.type, handlePhonemeByIdError, fetchPhonemeByIdSaga),
        takeLatest(fetchPhonemeSkillMapRequest.type, handlePhonemeSkillMapError, fetchPhonemeSkillMapSaga),

        // Analytics
        takeLatest(fetchUserProfileRequest.type, handleUserProfileError, fetchUserProfileSaga),
        takeLatest(fetchAnalyticsRequest.type, handleAnalyticsError, fetchAnalyticsSaga),
        takeLatest(fetchProgressTimelineRequest.type, handleProgressTimelineError, fetchProgressTimelineSaga),
    ]);
}
